(function () {
    'use strict';

    angular
        .module('app')
        .factory('UnsavedChangesService', UnsavedChangesService);

    UnsavedChangesService.$inject = ['$rootScope', '$location', '$window', '$q', 'DialogService'];

    /**
     * @ngdoc service
     * @name UnsavedChangesService
     * 
     * @description
     * The unsaved changes service helps detect unsaved changes for a model, and prompts a
     * "Do you wish to save changes?" dialog to the user if they attempt to navigate away
     * from the page.
     *
     * If user clicks Yes:
     * saveFunction is called
     * if promise returned from saveFunction is resolved, navigation continues
     * if promise returned from saveFunction is rejected, navigation is cancelled
     *
     * If user clicks No:
     * navigation is cancelled
     *
     * Use the "register" function to configure the service. Calling "register" when there
     * is already a registration will replace the registration.
     *
     * NB: This may give unexpected results if the model evaluates to null.
     */
    function UnsavedChangesService($rootScope, $location, $window, $q, DialogService) {
        var defaultRegistration = {
            callback: null,
            scope: null,
            modelExpression: null,
            saveFunction: null,
            unsavedItemName: 'item',
            originalModel: null,
            skipDirtyCheck: false
        };

        var registration = angular.copy(defaultRegistration);

        var service = {
            register: register,
            clearRegistration: clearRegistration,
            get dirty() { return getDirty(); },
            set dirty(value) { setDirty(value); },
            promptUnsavedChanges: promptUnsavedChanges,
            get skipDirtyCheck() { return registration.skipDirtyCheck; },
            set skipDirtyCheck(value) { registration.skipDirtyCheck = value; }
        };

        activate();

        return service;

        /* ************************************************ */

        /**
         * Register for an unsaved changes prompt. When the user attempts to navigate to
         * another page/view, if the model has unsaved changes an "Unsaved Changes" prompt
         * will be presented to the user with Yes/No options.
         *
         * When this function is first called, if a modelExpression has been provided,
         * the model is considered *not* dirty. Use the "dirty" property to specify otherwise.
         *
         *
         * @param {object} registrationDetails - Object containing the following properties:
         *
         * {Object} scope - The scope for the controller containing the model.
         * when the scope is destroyed, the registration is cleared.
         *
         * {string} modelExpression - An expression to retrieve the model from the
         * scope, e.g. 'vm.myModel'.
         * Optional. Provide this *or* getDirtyFunction.
         *
         * {Function} getDirtyFunction - A function returning a bool to indicate whether
         * there are unsaved changes.
         * Optional. Provide this *or* modelExpression.
         *
         * {Function} saveFunction - The function to be called when the user clicks
         * 'Yes' on the unsaved changes prompt. MUST return a promise that is resolved
         * if the save succeeds, and rejected if the save fails.
         * Optional. Provide this *or* onLocationChange.
         *
         * {Function} onLocationChange - A function to be called when the user tries
         * to navigate to a new location. To delay location change, return a promise
         * that resolves once navigation can proceed. To proceed with navigation immediately,
         * return anything else. To prevent navigation, return a rejected promise.
         * This function is called regardless of whether the object is dirty, or
         * skipDirtyCheck is true.
         * Optional. Provide this *or* saveFunction.
         *
         * {string} unsavedItemName - The name of the item (e.g. Note). Optional.
         */
        function register(registrationDetails) {
            if (!_.isObject(registrationDetails)) {
                throw new Error('UnsavedChangesService.register must be provided with registrationDetails object');
            }

            if (_.isNil(registrationDetails.scope)) {
                throw new Error('UnsavedChangesService.register must be provided with a scope');
            }

            if (!_.isNil(registrationDetails.modelExpression) && !_.isNil(registrationDetails.getDirtyFunction)) {
                throw new Error('UnsavedChangesService.register must be provided with modelExpression or getDirtyFunction, not both');
            }

            if (_.isNil(registrationDetails.modelExpression) && _.isNil(registrationDetails.getDirtyFunction)) {
                throw new Error('UnsavedChangesService.register must be provided with modelExpression or getDirtyFunction');
            }

            if (!_.isNil(registrationDetails.modelExpression) && !_.isString(registrationDetails.modelExpression)) {
                throw new Error('UnsavedChangesService.register has been provided a modelExpression that is not a string');
            }

            if (!_.isNil(registrationDetails.getDirtyFunction) && !_.isFunction(registrationDetails.getDirtyFunction)) {
                throw new Error('UnsavedChangesService.register has been provided a getDirtyFunction that is not a function');
            }

            if (!_.isNil(registrationDetails.saveFunction) & !_.isNil(registrationDetails.onLocationChange)) {
                throw new Error('UnsavedChangesService.register must be provided with saveFunction or onLocationChange, not both');
            }

            if (_.isNil(registrationDetails.saveFunction) & _.isNil(registrationDetails.onLocationChange)) {
                throw new Error('UnsavedChangesService.register must be provided with saveFunction or onLocationChange');
            }

            if (!_.isNil(registrationDetails.saveFunction) && !_.isFunction(registrationDetails.saveFunction)) {
                throw new Error('UnsavedChangesService.register has been provided a saveFunction that is not a function');
            }

            if (!_.isNil(registrationDetails.onLocationChange) && !_.isFunction(registrationDetails.onLocationChange)) {
                throw new Error('UnsavedChangesService.register has been provided a onLocationChange that is not a function');
            }

            clearRegistration();

            registration.scope = registrationDetails.scope;
            registration.modelExpression = registrationDetails.modelExpression;
            registration.callback = registrationDetails.getDirtyFunction;
            registration.saveFunction = registrationDetails.saveFunction;
            registration.onLocationChange = registrationDetails.onLocationChange;
            registration.unsavedItemName = registrationDetails.unsavedItemName ? registrationDetails.unsavedItemName : registration.unsavedItemName;

            if (!_.isNil(registrationDetails.modelExpression)) {
                registration.originalModel = angular.copy(registrationDetails.scope.$eval(registrationDetails.modelExpression));
            }

            registration.scope.$on('$destroy', clearRegistration);
        }

        /**
         * Clears the unsaved changes registration. No 'unsaved changes' prompt will be
         * displayed after this function is called.
         */
        function clearRegistration() {
            registration = angular.copy(defaultRegistration);
        }

        function getDirty() {
            if (angular.isFunction(registration.callback)) {
                return registration.callback();
            }

            if (registration.scope === null || registration.modelExpression === null) {
                return false;
            }

            if (registration.originalModel === null) {
                return true;
            }

            return !(objectsEqual(registration.originalModel, registration.scope.$eval(registration.modelExpression)));
        }

        function setDirty(value) {
            if (angular.isFunction(registration.callback)) {
                throw new Error('Cannot set dirty property of UnsavedChangesService when using a callback registration.');
            }

            if (registration.scope === null) {
                throw new Error('Cannot set dirty property of UnsavedChangesService without a registered scope.');
            }

            if (value) {
                registration.originalModel = null;
            } else {
                registration.originalModel = angular.copy(registration.scope.$eval(registration.modelExpression));
            }
        }

        /**
         * If there are unsaved changes, display an "Unsaved Changes" dialog / prompt.
         * Or, if registered, call the registered 'onLocationChange' function.
         * 
         * @return {Promise} Resolves if the user:
         *   - saves the unsaved changes
         *   - chooses to ignore them
         *   - there are no unsaved changes
         *   Rejects if the user "cancels" the prompt.
         */
        function promptUnsavedChanges() {
            var promise = promptUnsavedChangesInner();

            if (!_.isNil(promise) && _.isFunction(promise.then)) {
                return promise;
            } else {
                return $q.resolve();
            }
        }

        /**
         * If there are unsaved changes, display an "Unsaved Changes" dialog / prompt.
         * Or, if registered, call the registered 'onLocationChange' function.
         * 
         * @return {Promise|undefined} Resolves if the user:
         *   - saves the unsaved changes
         *   - chooses to ignore them
         *   - there are no unsaved changes
         *   Rejects if the user "cancels" the prompt.
         *   OR, returns undefined if there was no need for a promise and navigation can continue.
         */
        function promptUnsavedChangesInner() {
            if (!_.isNil(promptUnsavedChangesInner.currentPromise)) {
                return promptUnsavedChangesInner.currentPromise;
            }

            if (registration.skipDirtyCheck) {
                return;
            }

            var promiseChain;

            if (_.isFunction(registration.onLocationChange)) {
                // Use the registered callback to determine the course of action
                var locationChangePromise = registration.onLocationChange();

                if (!_.isNil(locationChangePromise) && _.isFunction(locationChangePromise.then)) {
                    promiseChain = locationChangePromise;
                } else {
                    return;
                }
            } else if (service.dirty) {
                // Show an "Unsaved Changes" dialog to let the user determine the course of action
                promiseChain = DialogService.unsavedChanges(registration.unsavedItemName, true)
                    .then(unsavedChangesDialogOK);
            } else {
                // There are no unsaved changes
                return;
            }

            promiseChain.finally(function () {
                promptUnsavedChangesInner.currentPromise = null;
            });

            promptUnsavedChangesInner.currentPromise = promiseChain;
            return promiseChain;

            function unsavedChangesDialogOK(itemShouldBeSaved) {
                if (itemShouldBeSaved) {
                    return registration.saveFunction();
                } else {
                    return $q.resolve();
                }
            }
        }

        /* ************************************************ */

        function activate() {

            // The onbeforeunload event is used to detect window reload, bookmark navigation,
            // tab close, and any other events that $locationChangeStart doesn't catch.
            $window.onbeforeunload = function (e) {
                if (!registration.skipDirtyCheck && service.dirty) {
                    var message = 'The item has unsaved changes. Are you sure you wish to continue?';
                    if (registration.unsavedItemName) {
                        message = 'The ' + registration.unsavedItemName + ' has unsaved changes. Are you sure you wish to continue?';
                    }

                    e.returnValue = message;
                    return message;
                }
            };

            $rootScope.$on('$locationChangeStart', function (event, newUrl) {
                var unsavedChangesPromise = promptUnsavedChangesInner();

                if (!_.isNil(unsavedChangesPromise) && _.isFunction(unsavedChangesPromise.then)) {
                    event.preventDefault();
                    unsavedChangesPromise.then(function () {
                        navigateToDestination(newUrl);
                    });
                }
            });
        }

        function navigateToDestination(url) {
            registration.skipDirtyCheck = true;
            $window.location.href = url;
        }

        /*
         * Checks to see if the two supplied objects are equal with the following conditions:
         *      Private properties are ignored (prefixed with '$')
         *      Empty string, null and undefined are considered equal
         *      Array and Objects are considered equal (however their properties will be compared for equality)
         *
         *  @param {object} obj1 - First object, this will be checked for equality with obj2
         *  @param {object} obj2 - Second object, this will be checked for equality with obj1
         *
         *  @returns {bool} - Returns true if the objects are equal, otherwise false is returned.
         */
        function objectsEqual(obj1, obj2) {
            var equal = true;

            if ((_.isNil(obj1) || obj1 === '') && (_.isNil(obj2) || obj2 === '')) {
                // Objects have not been set with a value, so they will be the same for comparison purposes
            } else if ((_.isNil(obj1) || obj1 === '') && !(_.isNil(obj2) || obj2 === '')) {
                // obj1 does not have a value, however obj2 does.
                equal = false;
            } else if (!(_.isNil(obj1) || obj1 === '') && (_.isNil(obj2) || obj2 === '')) {
                // obj2 does not have a value, however obj1 does.
                equal = false;
            } else if (_.isObject(obj1) && !_.isObject(obj2)) {
                // obj1 is an array/object, however obj2 is not array/object
                equal = false;
            } else if (!_.isObject(obj1) && _.isObject(obj2)) {
                // obj2 is an array/object, however obj1 is not array/object
                equal = false;
            } else if (!_.isObject(obj1) && !_.isObject(obj2)) {
                // The objects are not array/object and will be compared directly
                if (!_.isEqual(obj1, obj2)) {
                    equal = false;
                }
            } else {
                // Both objects are either array or object type. Iterate over the properties and recursively compare the values
                // Private properties (starting with '$' are ignored).
                _.forEach(Object.keys(obj1), function (key) {
                    if (!_.startsWith(key, '$') && !objectsEqual(obj1[key], obj2[key])) {
                        equal = false;
                    }
                });

                _.forEach(Object.keys(obj2), function (key) {
                    if (!_.startsWith(key, '$') && !objectsEqual(obj1[key], obj2[key])) {
                        equal = false;
                    }
                });
            }

            return equal;
        }
    }
})();